import { useEffect, useMemo, useSyncExternalStore } from 'react';
import type {
  Conversation,
  CrmContact,
  DispatchBooking,
  LatestContactBookings,
  SubjectBooking,
  SubjectBookings,
} from '../../../api/types';
import type { CommsSource } from '../source';
import { useModuleRefresh } from '../useModuleRefresh';

/**
 * Each sender's last reservation — the one most recently made — for the chip on
 * a mail list row.
 *
 * A list repaints constantly — every socket patch, every archive, every reload
 * after an action — and the read behind this chip is one the service does not
 * cache, against a database we do not own. So the rows ask in BATCHES (one
 * request per page, never one per row), each answer is kept for a minute, and
 * a contact already being asked about is not asked about twice. A minute is
 * the right staleness for a chip a person glances at; the reading view and the
 * contact panel read fresh, and the module Refresh forces this one too.
 *
 * A read that could not run says nothing new: what was known is kept, and the
 * contacts wait out the minute before being asked again — re-asking a dispatch
 * that is down on every repaint is how a list makes an outage worse.
 */
export const LATEST_TTL_MS = 60_000;
/** The service drops ids past this, so it is also the size of one request. */
export const LATEST_BATCH = 50;

type BatchAnswer = { payload: readonly unknown[]; unavailable: string | null };

/**
 * The batching, the minute and the in-flight guard, for any per-id read a list
 * makes for its rows: the last reservation per CONTACT and the reservation a
 * subject names per CONVERSATION are the same problem with a different key.
 * `entriesOf` turns one answer into the ids it answered.
 */
export function createBatchedReads<R extends BatchAnswer, V>(
  entriesOf: (answer: R) => Iterable<readonly [number, V | null]>,
  now: () => number = Date.now,
) {
  const cache = new Map<number, { booking: V | null; at: number }>();
  const asking = new Set<number>();
  const listeners = new Set<() => void>();
  let version = 0;

  const notify = () => {
    version += 1;
    for (const listener of listeners) listener();
  };

  const keep = (ids: readonly number[], at: number) => {
    for (const id of ids) cache.set(id, { booking: cache.get(id)?.booking ?? null, at });
  };

  /** Ask about whichever of `ids` is unknown or older than the TTL. */
  const ensure = (fetch: (ids: readonly number[]) => Promise<R>, ids: readonly number[], { force = false } = {}) => {
    const at = now();
    const wanted = [...new Set(ids)].filter((id) => {
      if (asking.has(id)) return false;
      const hit = cache.get(id);
      return force || !hit || at - hit.at >= LATEST_TTL_MS;
    });
    const runs: Promise<void>[] = [];
    for (let start = 0; start < wanted.length; start += LATEST_BATCH) {
      const chunk = wanted.slice(start, start + LATEST_BATCH);
      for (const id of chunk) asking.add(id);
      runs.push(
        fetch(chunk)
          .then(
            (result) => {
              if (result.unavailable) return keep(chunk, now());
              const answered = new Map(entriesOf(result));
              const settled = now();
              // Asked about and not answered is an unlinked contact, a subject
              // that names nothing, or one this agent may not read — nothing to
              // draw either way.
              for (const id of chunk) cache.set(id, { booking: answered.get(id) ?? null, at: settled });
            },
            () => keep(chunk, now()),
          )
          .finally(() => {
            for (const id of chunk) asking.delete(id);
            notify();
          }),
      );
    }
    return Promise.all(runs).then(() => undefined);
  };

  return {
    ensure,
    /**
     * Drop what is known about one contact, so the next render asks again. An
     * agent who just re-linked a contact to the right customer must not watch
     * the wrong customer's last ride sit on the row for the rest of the minute.
     */
    forget: (id: number) => {
      if (cache.delete(id)) notify();
    },
    /** The answer, null for "nothing to show", undefined for "not asked yet". */
    get: (id: number) => cache.get(id)?.booking,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    version: () => version,
  };
}

/** Each sender's last reservation, by contact id. */
export const createLatestBookings = (now: () => number = Date.now) =>
  createBatchedReads<LatestContactBookings, DispatchBooking>(
    (answer) => answer.payload.map((row) => [row.contactId, row.booking] as const),
    now,
  );

/** The reservation each mail thread's subject names, by conversation (display) id. */
export const createSubjectBookings = (now: () => number = Date.now) =>
  createBatchedReads<SubjectBookings, SubjectBooking>(
    (answer) => answer.payload.map((row) => [row.conversationId, row] as const),
    now,
  );

/**
 * One store per SOURCE: a contact id means one person to the live API and
 * another to the preview's fixtures, and the two must never answer for each
 * other. In the app the source lives as long as the module, so this is one
 * store for the session.
 */
const stores = new WeakMap<CommsSource, ReturnType<typeof createLatestBookings>>();
const storeFor = (source: CommsSource) => {
  let store = stores.get(source);
  if (!store) {
    store = createLatestBookings();
    stores.set(source, store);
  }
  return store;
};

/** After a link changes who a contact is: see `forget`. */
export const forgetLatestBooking = (source: CommsSource, contactId: number) =>
  storeFor(source).forget(contactId);

/**
 * Whether a contact has a dispatch booker to ask about: a linked customer, or
 * an affiliate desk an agent said this address books for. The service reads
 * the same two links (services/contactLink.js bookerOf).
 */
export const hasDispatchBooker = (contact: Pick<CrmContact, 'dispatchCustomerId' | 'dispatchAffiliateId'>) =>
  Boolean(contact.dispatchCustomerId || contact.dispatchAffiliateId);

/**
 * The last reservation per contact for the rows on screen, by contact id. Only
 * contacts LINKED to a dispatch booker are asked about: everyone else has no
 * bookings to find, and naming them would cost the request a row for nothing.
 */
export function useLatestBookings(
  source: CommsSource,
  rows: readonly Conversation[],
): ReadonlyMap<number, DispatchBooking> {
  const store = storeFor(source);
  const ids = useMemo(
    () => [...new Set(rows.filter((row) => hasDispatchBooker(row.contact)).map((row) => row.contact.id))],
    [rows],
  );
  // Re-checked whenever the rows move, which costs nothing while the answers
  // are fresh — and is what lets a list left open past the minute catch up.
  useEffect(() => {
    if (ids.length) void store.ensure(source.api.listLatestContactBookings, ids);
  }, [store, source, ids]);
  useModuleRefresh(() => {
    if (ids.length) void store.ensure(source.api.listLatestContactBookings, ids, { force: true });
  });
  const version = useSyncExternalStore(store.subscribe, store.version, store.version);
  return useMemo(() => {
    const found = new Map<number, DispatchBooking>();
    for (const id of ids) {
      const booking = store.get(id);
      if (booking) found.set(id, booking);
    }
    return found;
    // `version` is the store's revision: a new answer for the same ids.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, ids, version]);
}

const subjectStores = new WeakMap<CommsSource, ReturnType<typeof createSubjectBookings>>();
const subjectStoreFor = (source: CommsSource) => {
  let store = subjectStores.get(source);
  if (!store) {
    store = createSubjectBookings();
    subjectStores.set(source, store);
  }
  return store;
};

/** The subject a mail thread carries, or '' for every other channel. */
export const mailSubjectOf = (conversation: Pick<Conversation, 'additionalAttributes'>) => {
  const subject = conversation.additionalAttributes?.mailSubject;
  return typeof subject === 'string' ? subject.trim() : '';
};

/**
 * The reservation each mail row's SUBJECT names, by conversation id — which
 * the chip shows in place of the sender's last reservation. Only rows with a
 * subject are asked about (every other channel has none), linked or not: a
 * stranger replying to a confirmation names the trip as well as a customer does.
 * Same batching and minute as the last reservation, in a store of its own.
 */
export function useSubjectBookings(
  source: CommsSource,
  rows: readonly Conversation[],
): ReadonlyMap<number, SubjectBooking> {
  const store = subjectStoreFor(source);
  const ids = useMemo(() => [...new Set(rows.filter((row) => mailSubjectOf(row)).map((row) => row.id))], [rows]);
  useEffect(() => {
    if (ids.length) void store.ensure(source.api.listSubjectBookings, ids);
  }, [store, source, ids]);
  useModuleRefresh(() => {
    if (ids.length) void store.ensure(source.api.listSubjectBookings, ids, { force: true });
  });
  const version = useSyncExternalStore(store.subscribe, store.version, store.version);
  return useMemo(() => {
    const found = new Map<number, SubjectBooking>();
    for (const id of ids) {
      const named = store.get(id);
      if (named) found.set(id, named);
    }
    return found;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, ids, version]);
}
